import { nextTick, ref, type Ref } from 'vue'

const HTTP_TOO_MANY_REQUESTS = 429

const isRateLimitError = (error: unknown): boolean =>
  (error as { response?: { status?: number } } | null)?.response?.status === HTTP_TOO_MANY_REQUESTS

/**
 * Reactive reCAPTCHA challenge for forms: the backend rate-limits and answers 429,
 * the form then shows <ReCaptcha> and retries with `captchaToken`.
 * @param submitDisabled the form's own submit-disabled flag; held true until the captcha is solved.
 */
export function useCaptchaChallenge(submitDisabled: Ref<boolean>) {
  const showRecaptcha = ref(false)
  const reCaptchaToken = ref<string | undefined>(undefined)

  const onVerify = (token: string) => {
    reCaptchaToken.value = token
    submitDisabled.value = false
  }

  const onError = () => {
    submitDisabled.value = true
  }

  const onExpired = () => {
    reCaptchaToken.value = undefined
    submitDisabled.value = true
  }

  /** Call from the submit catch block. Returns true when the error was a 429 and the challenge is now shown. */
  const handleRateLimit = (error: unknown): boolean => {
    const limited = isRateLimitError(error)
    // v2 tokens are single-use: whatever the outcome, the backend already assessed this one.
    reCaptchaToken.value = undefined
    if (!limited) {
      showRecaptcha.value = false
      return false
    }
    submitDisabled.value = true
    if (showRecaptcha.value) {
      // v2 tokens are single-use and a solved widget never re-fires `verify`:
      // unmount/remount it (forms render it behind v-if) so the user gets a fresh challenge.
      showRecaptcha.value = false
      nextTick(() => {
        showRecaptcha.value = true
      })
    } else {
      showRecaptcha.value = true
    }
    return true
  }

  const reset = () => {
    showRecaptcha.value = false
  }

  return { showRecaptcha, reCaptchaToken, onVerify, onError, onExpired, handleRateLimit, reset }
}
